// Post Card Styles
// Reddit-inspired post card styling

import styled, { css } from 'styled-components';
import { theme } from '../theme';
import { ChatRoomTheme } from '../../contexts/ChatRoomThemeContext';

export const PostCardContainer = styled.div<{ $theme?: ChatRoomTheme }>`
  background: ${props => props.$theme === 'light' 
    ? 'rgba(0, 0, 0, 0.02)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$theme === 'light' 
    ? 'rgba(0, 0, 0, 0.1)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$theme === 'light' 
      ? 'rgba(0, 0, 0, 0.04)' 
      : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${props => props.$theme === 'light' 
      ? 'rgba(0, 0, 0, 0.15)' 
      : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem;
    gap: 0.625rem;
    margin-bottom: 0.625rem;
  }
`;

export const PostVoteSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  min-width: 40px;
  flex-shrink: 0;
`;

export const VoteButton = styled.button<{ $isActive?: boolean; $direction: 'up' | 'down'; $theme?: ChatRoomTheme }>`
  background: none;
  border: none;
  color: ${({ $isActive, $direction, $theme }) => {
    if ($isActive && $direction === 'up') return theme.colors.primary;
    if ($isActive && $direction === 'down') return '#4A90E2';
    return $theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)';
  }};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1.25rem;

  &:hover {
    background: ${props => props.$theme === 'light' 
      ? 'rgba(0, 0, 0, 0.05)' 
      : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ $direction }) => ($direction === 'up' ? theme.colors.primary : '#4A90E2')};
  }

  &:active {
    transform: scale(0.9);
  }
`;

export const VoteCount = styled.div<{ $isPositive?: boolean; $theme?: ChatRoomTheme }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $isPositive, $theme }) => {
    if ($isPositive) return theme.colors.primary;
    return $theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)';
  }};
  min-height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PostContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export const PostTitle = styled.h3<{ $theme?: ChatRoomTheme }>`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.$theme === 'light' ? '#333' : 'white'};
  margin: 0;
  line-height: 1.4;
  cursor: pointer;

  &:hover {
    color: ${theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

export const PostBody = styled.p<{ $showFull?: boolean; $theme?: ChatRoomTheme }>`
  font-size: 0.9rem;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'};
  margin: 0;
  line-height: 1.5;
  ${({ $showFull }) =>
    !$showFull
      ? `
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `
      : `
    display: block;
  `}

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    ${({ $showFull }) => !$showFull && `-webkit-line-clamp: 2;`}
  }
`;

export const PostMeta = styled.div<{ $theme?: ChatRoomTheme }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'};
  margin-top: 0.25rem;
`;

export const PostAuthor = styled.span<{ $theme?: ChatRoomTheme; $clickable?: boolean }>`
  font-weight: 500;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'};

  /* A name that opens a profile has to look like it does something. Underlined
     on hover rather than always, so a wall of posts is not a wall of links. */
  ${props => props.$clickable && css`
    cursor: pointer;

    &:hover,
    &:focus-visible {
      text-decoration: underline;
      color: ${props.$theme === 'light' ? 'rgba(0, 0, 0, 1)' : 'rgba(255, 255, 255, 1)'};
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 3px;
    }
  `}
`;

export const PostTime = styled.span``;

export const PostCommentCount = styled.button<{ $theme?: ChatRoomTheme }>`
  background: none;
  border: none;
  color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;

  &:hover {
    background: ${props => props.$theme === 'light' 
      ? 'rgba(0, 0, 0, 0.05)' 
      : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'};
  }
`;

export const PostActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

export const SaveButton = styled.button<{ $isSaved: boolean; $theme?: ChatRoomTheme }>`
  background: none;
  border: none;
  color: ${({ $isSaved, $theme }) => {
    if ($isSaved) return theme.colors.primary;
    return $theme === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)';
  }};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1rem;

  &:hover {
    background: ${props => props.$theme === 'light' 
      ? 'rgba(0, 0, 0, 0.05)' 
      : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ $isSaved, $theme }) => {
      if ($isSaved) return theme.colors.primary;
      return $theme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)';
    }};
  }
`;

export const PostCardLink = styled.div`
  text-decoration: none;
  color: inherit;
`;

